import { useMapEvents } from 'react-leaflet';
import { useRef, useEffect, useCallback } from 'react';
import type { Position } from '../../types.js';
import type { LeafletMouseEvent } from 'leaflet';

interface MapEventsProps {
  onMapClick: (position: Position, pointType: 'via' | 'end') => void;
  onContextMenu: (e: LeafletMouseEvent) => void;
  onCloseContextMenu?: () => void;
  viewEditMode?: 'view' | 'edit'; // Add view/edit mode prop
}

export const MapEvents: React.FC<MapEventsProps> = ({ onMapClick, onContextMenu, onCloseContextMenu, viewEditMode = 'edit' }) => {
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastClickTimeRef = useRef<number>(0);
  const clickPositionRef = useRef<{ lat: number; lng: number } | null>(null);
  
  // Touch handling for double-tap-and-hold zoom
  const touchStartYRef = useRef<number>(0);
  const isDoubleTapHoldRef = useRef<boolean>(false);
  const zoomIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialZoomRef = useRef<number>(0); // Store initial zoom level for position-based zoom
  const mapDragWasEnabledRef = useRef<boolean>(true); // Track if dragging was enabled before we disabled it
  const holdModeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Timeout to allow hold gesture to start
  const lastZoomTimeRef = useRef<number>(0); // Throttle zoom calls for smoothness
  const clickSuppressUntilRef = useRef<number>(0); // Prevent point placement immediately after double-tap

  // Define the map first so it's available to all handler functions
  const map = useMapEvents({
    click: handleMapClick,
    contextmenu(e) {
      onContextMenu(e);
    }
  });

  // Simplified smooth zoom without conflicting animations
  const applySimpleSmoothZoom = useCallback((targetZoomLevel: number) => {
    if (!map) return;
    
    // Simple, direct zoom without competing transforms
    map.setZoom(targetZoomLevel);
    
  }, [map]);

  function handleMapClick(e: LeafletMouseEvent) {
    const currentTime = Date.now();
    const timeSinceLastClick = currentTime - lastClickTimeRef.current;
    const clickPosition = { lat: e.latlng.lat, lng: e.latlng.lng };
    
    // Close context menu if it's open
    if (onCloseContextMenu) {
      onCloseContextMenu();
    }

    // If we're in double-tap-hold mode, ignore this click completely
    if (isDoubleTapHoldRef.current) {
      return;
    }

    // Check for double-tap (this should work in both view and edit modes)
    if (timeSinceLastClick < 300 && timeSinceLastClick > 50) {
      
      // Clear the pending route point placement from first tap
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      
      // Update timing for this double-tap
      lastClickTimeRef.current = currentTime;
      clickPositionRef.current = clickPosition;
      
      // Perform ADAPTIVE enhanced zoom - larger steps when zoomed out, smaller when zoomed in
      const currentZoom = map.getZoom();
      const minZoom = map.getMinZoom();
      const maxZoom = map.getMaxZoom();
      
      // Calculate adaptive zoom delta based on current zoom level
      // When zoomed out (close to minZoom): larger steps (2-3 levels)
      // When zoomed in (close to maxZoom): smaller steps (0.5-1 level)
      const zoomRange = maxZoom - minZoom;
      const currentPosition = (currentZoom - minZoom) / zoomRange; // 0 = fully zoomed out, 1 = fully zoomed in
      
      // Inverted curve: starts high when zoomed out, decreases as we zoom in
      const adaptiveZoomDelta = 3 - (currentPosition * 2.5); // Range from 3.0 to 0.5
      const targetZoom = Math.min(currentZoom + adaptiveZoomDelta, maxZoom);
      
      // Use flyTo for smoother animation instead of setZoom
      map.flyTo(map.getCenter(), targetZoom, {
        animate: true,
        duration: 0.3 // Short, smooth animation
      });
      // Do NOT enter hold mode for a simple double tap; just suppress route placement briefly
      clickSuppressUntilRef.current = Date.now() + 350; // 350ms suppression window
      return;
    }

    // After double-tap suppression window, skip placement if still within window
    if (Date.now() < clickSuppressUntilRef.current) {
      return;
    }

    // Call onMapClick for highlight animation in view mode only
    if (viewEditMode === 'view') {
      onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng }, 'end');
      // Still update timing for view mode to enable double-tap detection
      lastClickTimeRef.current = currentTime;
      clickPositionRef.current = clickPosition;
      return;
    }

    // Check if we're in double-tap-hold mode (don't place route points)
    if (isDoubleTapHoldRef.current) {
      return;
    }

    // Check if this is a new location (use previous position before updating)
    const isSameLocation = clickPositionRef.current && 
      Math.abs(clickPosition.lat - clickPositionRef.current.lat) < 0.0001 &&
      Math.abs(clickPosition.lng - clickPositionRef.current.lng) < 0.0001;

    // Update click timing and position for next comparison
    lastClickTimeRef.current = currentTime;
    clickPositionRef.current = clickPosition;

    if (!isSameLocation) {
      
      // Clear any existing timeout
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }

      // Set a delay before placing the route point to detect double tap
      clickTimeoutRef.current = setTimeout(() => {
        onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng }, 'end');
        clickTimeoutRef.current = null;
      }, 250); // 250ms delay to detect second tap
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      if (zoomIntervalRef.current) {
        clearInterval(zoomIntervalRef.current);
      }
      if (holdModeTimeoutRef.current) {
        clearTimeout(holdModeTimeoutRef.current);
      }
    };
  }, []);

  // Add touch event listeners to the map container
  useEffect(() => {
    if (map) {
      const container = map.getContainer();
      
      // Define handlers directly in useEffect to avoid dependency issues
      const touchStartHandler = (e: TouchEvent) => {
        const currentTime = Date.now();
        const timeSinceLastClick = currentTime - lastClickTimeRef.current;
        
        // Check if this might be the second tap of a double-tap
        if (timeSinceLastClick < 500 && timeSinceLastClick > 30 && lastClickTimeRef.current > 0) {
          
          // Clear any pending route point placement from first tap
          if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
          }
          
          // Mark as double-tap-hold mode
          isDoubleTapHoldRef.current = true;
          touchStartYRef.current = e.touches[0].clientY;
          
          // Capture initial zoom level for position-based calculation
          initialZoomRef.current = map.getZoom();

        }
      };
      
      const touchMoveHandler = (e: TouchEvent) => {
        
        if (!isDoubleTapHoldRef.current || !map) {
          return;
        }
        
        // PREVENT MAP PANNING during double-tap-hold zoom
        e.preventDefault();
        e.stopPropagation();
        
        const currentY = e.touches[0].clientY;
        const deltaY = touchStartYRef.current - currentY;
        
        // Apply zoom - simple logic without timeout checks
        if (Math.abs(deltaY) > 5) { // Reduced threshold for better responsiveness
          // Balanced throttling for smooth performance (120fps)
          const now = performance.now();
          if (now - lastZoomTimeRef.current < 8) return; // ~120fps - smooth but not overwhelming
          lastZoomTimeRef.current = now;
          
          // ULTRA-SMOOTH zoom progression - micro-adjustments for continuous feel
          const baseZoom = initialZoomRef.current;
          const maxZoomChange = 1.5; // Even smaller for ultra-precise control
          
          // SAFETY CHECK: Ensure baseZoom is valid
          if (!baseZoom || isNaN(baseZoom)) {
            return;
          }
          
          // IMPORTANT: deltaY > 0 means finger moved UP = ZOOM IN
          // Use smaller increments for continuous feel
          const clampedDeltaY = Math.max(-150, Math.min(deltaY, 150)); // Reduced range
          const zoomChange = (clampedDeltaY / 150) * maxZoomChange; // Ultra-fine scaling
          const targetZoom = baseZoom + zoomChange;
          
          const clampedZoom = Math.max(
            map.getMinZoom(),
            Math.min(targetZoom, map.getMaxZoom())
          );
          
          // EMERGENCY BRAKE: Prevent extreme zoom changes
          const maxAllowedChange = 5; // Never change more than 5 levels at once
          const actualChange = Math.abs(clampedZoom - baseZoom);
          if (actualChange > maxAllowedChange) {
            return;
          }


          // Use simple smooth zoom without competing animations
          applySimpleSmoothZoom(clampedZoom);
        }
      };
      
      const touchEndHandler = () => {

        
        // Clear hold timeout
        if (holdModeTimeoutRef.current) {
          clearTimeout(holdModeTimeoutRef.current);
          holdModeTimeoutRef.current = null;
        }
              
        // Reset double-tap-hold flag
        if (isDoubleTapHoldRef.current) {
          isDoubleTapHoldRef.current = false;
        }
      };
    
      // Add debug touchmove listener to document to see if ANY touchmove events fire at all
      const globalDebugTouchMoveHandler = (e: TouchEvent) => {
        
        // If we're in double-tap-hold mode, FORCE zoom to work from global handler
        if (isDoubleTapHoldRef.current) {
          
          // PREVENT map panning during zoom
          e.preventDefault();
          e.stopPropagation();
          
          const currentY = e.touches[0].clientY;
          const deltaY = touchStartYRef.current - currentY;
          
          if (Math.abs(deltaY) > 5) { // Reduced threshold for better responsiveness
            // Balanced throttling for smooth performance (120fps)
            const now = performance.now();
            if (now - lastZoomTimeRef.current < 8) return; // ~120fps - smooth but not overwhelming
            lastZoomTimeRef.current = now;
            
            // ULTRA-SMOOTH zoom progression - micro-adjustments for continuous feel (same as main handler)
            const baseZoom = initialZoomRef.current;
            const maxZoomChange = 1.5; // Even smaller for ultra-precise control
            
            // SAFETY CHECK: Ensure baseZoom is valid
            if (!baseZoom || isNaN(baseZoom)) {
              return;
            }
            
            // IMPORTANT: deltaY > 0 means finger moved UP = ZOOM IN
            const clampedDeltaY = Math.max(-150, Math.min(300, deltaY)); // Reduced range
            const zoomChange = (clampedDeltaY / 150) * maxZoomChange; // Ultra-fine scaling
            const targetZoom = baseZoom + zoomChange;
            const clampedZoom = Math.max(
              map.getMinZoom(),
              Math.min(targetZoom, map.getMaxZoom())
            );
            
            // EMERGENCY BRAKE: Prevent extreme zoom changes
            const maxAllowedChange = 5; // Never change more than 5 levels at once
            const actualChange = Math.abs(clampedZoom - baseZoom);
            if (actualChange > maxAllowedChange) {
              return;
            }

            // Use simple smooth zoom without competing animations
            applySimpleSmoothZoom(clampedZoom);
          }
        }
      };
      
      // Mouse event handlers for desktop testing
      const mouseDownHandler = (e: MouseEvent) => {
        const currentTime = Date.now();
        const timeSinceLastClick = currentTime - lastClickTimeRef.current;
        
        
        // Same double-tap logic but for mouse
        if (timeSinceLastClick < 500 && timeSinceLastClick > 30 && lastClickTimeRef.current > 0) {
          
          if (clickTimeoutRef.current) {
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
          }
          
          isDoubleTapHoldRef.current = true;
          touchStartYRef.current = e.clientY;
          
          if (map.dragging && map.dragging.enabled()) {
            mapDragWasEnabledRef.current = true;
            map.dragging.disable();
          }
          
          initialZoomRef.current = map.getZoom();
          
          holdModeTimeoutRef.current = setTimeout(() => {
            if (isDoubleTapHoldRef.current) {
              isDoubleTapHoldRef.current = false;
              if (map && map.dragging && mapDragWasEnabledRef.current) {
                map.dragging.enable();
              }
            }
          }, 500);
        }
      };
      
      const mouseMoveHandler = (e: MouseEvent) => {
        
        if (!isDoubleTapHoldRef.current || !map) {
          return;
        }
        
        e.preventDefault();
        
        if (holdModeTimeoutRef.current) {
          clearTimeout(holdModeTimeoutRef.current);
          holdModeTimeoutRef.current = null;
        }
        
        const currentY = e.clientY;
        const deltaY = touchStartYRef.current - currentY;
        
        // Apply zoom - same logic as touch
        if (Math.abs(deltaY) > 10) {
          const zoomSensitivity = 0.01;
          const zoomChange = deltaY * zoomSensitivity;
          const currentZoom = map.getZoom();
          const newZoom = Math.max(
            map.getMinZoom(),
            Math.min(currentZoom + zoomChange, map.getMaxZoom())
          );

          map.setZoom(newZoom);
        }
      };
      
      const mouseUpHandler = () => {
   
        if (holdModeTimeoutRef.current) {
          clearTimeout(holdModeTimeoutRef.current);
          holdModeTimeoutRef.current = null;
        }
        
        if (isDoubleTapHoldRef.current && map && map.dragging && mapDragWasEnabledRef.current) {
          map.dragging.enable();
        }
        
        if (isDoubleTapHoldRef.current) {
          isDoubleTapHoldRef.current = false;
        }
      };

      // Attach both touch and mouse listeners
      container.addEventListener('touchstart', touchStartHandler, { passive: true });
      container.addEventListener('touchmove', touchMoveHandler, { passive: false });
      container.addEventListener('touchend', touchEndHandler, { passive: true });
      
      // GLOBAL debug listener on document to catch ALL touchmove events
      document.addEventListener('touchmove', globalDebugTouchMoveHandler, { passive: false, capture: true });
      
  
      // Mouse listeners for desktop testing
      container.addEventListener('mousedown', mouseDownHandler, { passive: true });
      document.addEventListener('mousemove', mouseMoveHandler, { passive: false }); // Document for mousemove
      document.addEventListener('mouseup', mouseUpHandler, { passive: true });
          
      return () => {
        container.removeEventListener('touchstart', touchStartHandler);
        container.removeEventListener('touchmove', touchMoveHandler);
        container.removeEventListener('touchend', touchEndHandler);
        
        // Remove global debug listener
        document.removeEventListener('touchmove', globalDebugTouchMoveHandler, { capture: true });
              
        container.removeEventListener('mousedown', mouseDownHandler);
        document.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('mouseup', mouseUpHandler);
        
      };
    }
  }, [map]); // Only depend on map, not the handlers

  return null;
};
